import { useState } from "react";

import { Button, type Column, EmptyState, Modal, Pill, Select, Table, Toggle } from "@/components/common";
import { useCreateIncident, useIncidents, useUpdateIncident } from "@/hooks/useLgpd";
import type {
  Incident,
  IncidentSeverity,
  IncidentStatus,
  IncidentType,
} from "@/types/lgpd";

const TYPE_LABEL: Record<IncidentType, string> = {
  leak: "Vazamento",
  unauthorized_access: "Acesso indevido",
  data_loss: "Perda de dados",
  external_attack: "Ataque externo",
  system_error: "Erro de sistema",
  social_engineering: "Engenharia social",
};

const SEVERITY_LABEL: Record<IncidentSeverity, string> = {
  critical: "Crítica",
  high: "Alta",
  medium: "Média",
  low: "Baixa",
};

const SEVERITY_TONE: Record<IncidentSeverity, "alerta" | "inad" | "onb" | "pausada"> = {
  critical: "alerta",
  high: "inad",
  medium: "onb",
  low: "pausada",
};

const STATUS_LABEL: Record<IncidentStatus, string> = {
  identified: "Identificado",
  under_investigation: "Em investigação",
  mitigated: "Mitigado",
  resolved: "Resolvido",
  notified: "Notificado",
};

const STATUS_TONE: Record<IncidentStatus, "diag" | "onb" | "inad" | "ativa"> = {
  identified: "diag",
  under_investigation: "onb",
  mitigated: "inad",
  resolved: "ativa",
  notified: "ativa",
};

function dateTime(iso: string | null): string {
  if (!iso) return "—";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "—" : d.toLocaleString("pt-BR");
}

export function IncidentsPanel() {
  const { data: incidents = [], isLoading } = useIncidents();
  const [selected, setSelected] = useState<Incident | null>(null);
  const [creating, setCreating] = useState(false);

  const columns: Column<Incident>[] = [
    { id: "type", header: "Tipo", cell: (i) => <span className="td-muted">{TYPE_LABEL[i.type]}</span> },
    {
      id: "severity",
      header: "Severidade",
      cell: (i) => <Pill tone={SEVERITY_TONE[i.severity]}>{SEVERITY_LABEL[i.severity]}</Pill>,
    },
    {
      id: "status",
      header: "Status",
      cell: (i) => <Pill tone={STATUS_TONE[i.status]}>{STATUS_LABEL[i.status]}</Pill>,
    },
    {
      id: "records",
      header: "Registros",
      align: "right",
      cell: (i) => <span className="td-faded">{i.affected_records_count ?? "—"}</span>,
    },
    { id: "when", header: "Identificado", cell: (i) => <span className="td-faded">{dateTime(i.identified_at)}</span> },
  ];

  return (
    <>
      <div className="emp-toolbar">
        <span className="emp-toolbar-counter">{incidents.length} incidentes</span>
        <div className="emp-toolbar-spacer" />
        <Button variant="primary" iconLeft="plus" onClick={() => setCreating(true)}>
          Registrar incidente
        </Button>
      </div>
      <Table
        rows={incidents}
        columns={columns}
        getKey={(i) => String(i.id)}
        onRowClick={setSelected}
        empty={
          <EmptyState
            icon="alert-triangle"
            size="lg"
            title={isLoading ? "Carregando…" : "Nenhum incidente"}
            description={isLoading ? undefined : "Incidentes de segurança (Art. 48) aparecerão aqui."}
          />
        }
      />
      {creating && <CreateModal onClose={() => setCreating(false)} />}
      {selected && <UpdateModal incident={selected} onClose={() => setSelected(null)} />}
    </>
  );
}

function CreateModal({ onClose }: { onClose: () => void }) {
  const create = useCreateIncident();
  const [type, setType] = useState<IncidentType>("leak");
  const [severity, setSeverity] = useState<IncidentSeverity>("high");
  const [description, setDescription] = useState("");
  const [records, setRecords] = useState("");

  async function submit() {
    if (!description.trim()) return;
    await create.mutateAsync({
      type,
      severity,
      description: description.trim(),
      affected_records_count: records.trim() ? Number(records) : undefined,
    });
    onClose();
  }

  return (
    <Modal
      onClose={onClose}
      title="Registrar incidente"
      subtitle="Art. 48 — comunicação de incidente de segurança"
      size="md"
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Cancelar
          </Button>
          <Button variant="primary" onClick={() => void submit()} disabled={create.isPending || !description.trim()}>
            Registrar
          </Button>
        </>
      }
    >
      <div className="lgpd-detail">
        <label className="lgpd-field">
          <span>Tipo</span>
          <Select
            value={type}
            onChange={setType}
            options={(Object.keys(TYPE_LABEL) as IncidentType[]).map((t) => ({
              value: t,
              label: TYPE_LABEL[t],
            }))}
          />
        </label>
        <label className="lgpd-field">
          <span>Severidade</span>
          <Select
            value={severity}
            onChange={setSeverity}
            options={(Object.keys(SEVERITY_LABEL) as IncidentSeverity[]).map((s) => ({
              value: s,
              label: SEVERITY_LABEL[s],
            }))}
          />
        </label>
        <label className="lgpd-field">
          <span>Descrição</span>
          <textarea value={description} onChange={(e) => setDescription(e.target.value)} rows={3} />
        </label>
        <label className="lgpd-field">
          <span>Registros afetados (opcional)</span>
          <input
            type="number"
            min={0}
            value={records}
            onChange={(e) => setRecords(e.target.value)}
          />
        </label>
      </div>
    </Modal>
  );
}

function UpdateModal({ incident, onClose }: { incident: Incident; onClose: () => void }) {
  const update = useUpdateIncident();
  const [status, setStatus] = useState<IncidentStatus>(incident.status);
  const [measures, setMeasures] = useState(incident.measures_taken ?? "");
  const [anpd, setAnpd] = useState(incident.anpd_notified_at != null);
  const [subjects, setSubjects] = useState(incident.subjects_notified_at != null);

  async function submit() {
    await update.mutateAsync({
      id: incident.id,
      payload: {
        status,
        measures_taken: measures.trim() || undefined,
        mark_anpd_notified: anpd && incident.anpd_notified_at == null,
        mark_subjects_notified: subjects && incident.subjects_notified_at == null,
      },
    });
    onClose();
  }

  return (
    <Modal
      onClose={onClose}
      title={`Incidente #${incident.id}`}
      subtitle={`${TYPE_LABEL[incident.type]} · ${SEVERITY_LABEL[incident.severity]}`}
      size="md"
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Fechar
          </Button>
          <Button variant="primary" onClick={() => void submit()} disabled={update.isPending}>
            Salvar
          </Button>
        </>
      }
    >
      <div className="lgpd-detail">
        <div className="lgpd-detail-block">
          <span>Descrição</span>
          <p>{incident.description}</p>
        </div>
        <label className="lgpd-field">
          <span>Status</span>
          <Select
            value={status}
            onChange={setStatus}
            options={(Object.keys(STATUS_LABEL) as IncidentStatus[]).map((s) => ({
              value: s,
              label: STATUS_LABEL[s],
            }))}
          />
        </label>
        <label className="lgpd-field">
          <span>Medidas tomadas</span>
          <textarea value={measures} onChange={(e) => setMeasures(e.target.value)} rows={3} />
        </label>
        <Toggle
          checked={anpd}
          onChange={setAnpd}
          disabled={incident.anpd_notified_at != null}
          label="ANPD notificada"
          sub={incident.anpd_notified_at ? `Em ${dateTime(incident.anpd_notified_at)}` : "Marcar como notificada"}
        />
        <Toggle
          checked={subjects}
          onChange={setSubjects}
          disabled={incident.subjects_notified_at != null}
          label="Titulares notificados"
          sub={
            incident.subjects_notified_at
              ? `Em ${dateTime(incident.subjects_notified_at)}`
              : "Marcar como notificados"
          }
        />
      </div>
    </Modal>
  );
}
